import React, { Suspense, useRef, useCallback } from "react";
import { StatCards } from "./rich-blocks/StatCards";
import { BarDistribution } from "./rich-blocks/BarDistribution";
import { EntityList } from "./rich-blocks/EntityList";
import { PhaseTimeline } from "./rich-blocks/PhaseTimeline";
import { CalloutCard } from "./rich-blocks/CalloutCard";
import { ComparisonTable } from "./rich-blocks/ComparisonTable";
import { KpiCards } from "./rich-blocks/KpiCards";
import { GaugeRings } from "./rich-blocks/GaugeRings";
import { PropertyCard } from "./rich-blocks/PropertyCard";
import { BreakdownBars } from "./rich-blocks/BreakdownBars";
import { BlockPlaceholder } from "./rich-blocks/BlockPlaceholder";

// Lazy-load chart components to keep recharts out of the main bundle
const ScatterPlot = React.lazy(() => import("./rich-blocks/ScatterPlot"));

interface RichBlockProps {
  language: string;
  content: string;
  onEntityClick?: (entityType: string, entityId: string, name: string) => void;
}

export function RichBlock({ language, content, onEntityClick }: RichBlockProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);

  const handleAnimationEnd = useCallback(() => {
    wrapperRef.current?.classList.remove("cx-rich-block-enter");
  }, []);

  let block: React.ReactNode = null;

  switch (language) {
    case "stat":
      block = <StatCards content={content} />;
      break;
    case "bars":
      block = <BarDistribution content={content} />;
      break;
    case "entities":
      block = <EntityList content={content} onEntityClick={onEntityClick} />;
      break;
    case "timeline":
      block = <PhaseTimeline content={content} onEntityClick={onEntityClick} />;
      break;
    case "callout":
      block = <CalloutCard content={content} onEntityClick={onEntityClick} />;
      break;
    case "compare":
      block = <ComparisonTable content={content} onEntityClick={onEntityClick} />;
      break;
    case "kpi":
      block = <KpiCards content={content} />;
      break;
    case "gauge":
      block = <GaugeRings content={content} />;
      break;
    case "scatter":
      block = (
        <Suspense fallback={<BlockPlaceholder type="scatter" />}>
          <ScatterPlot content={content} onEntityClick={onEntityClick} />
        </Suspense>
      );
      break;
    case "property":
      block = <PropertyCard content={content} onEntityClick={onEntityClick} />;
      break;
    case "breakdown":
      block = <BreakdownBars content={content} />;
      break;
    default:
      return null;
  }

  return (
    <div ref={wrapperRef} className="cx-rich-block-enter" onAnimationEnd={handleAnimationEnd}>
      {block}
    </div>
  );
}
